import { type FC, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import axios from 'src/utils/axios';
import { messageForHouseholdMemberError } from 'src/utils/errors/contactErrorMessages';
import { formatPhoneNumber } from 'src/utils/formatNumber';
import type { UHMember, UhmRole } from 'src/types/Certification';
import {
  UHM_EDITABLE_ROLE_OPTIONS,
  UHM_ROLE_LABELS,
  getProfileMemberFullName,
} from './profileHouseholdUtils';

const emailRegex = /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/;

interface Language {
  id: number;
  name: string;
}

interface EditUserHouseholdMemberScreenProps {
  member: UHMember;
  onSaved: () => void;
  onCancel: () => void;
}

interface FormValues {
  first_name: string;
  middle_name: string;
  last_name: string;
  household_role: UhmRole;
  preferred_language: Language | null;
  email: string;
  phone_number: string;
}

const cardSx = {
  backgroundColor: 'background.paper',
  borderRadius: '16px',
  boxShadow: '0px 2px 10px rgba(0,0,0,0.063)',
};

const primaryEmailOf = (member: UHMember): string =>
  member.emails?.find(
    (e) => e.is_primary && !e.email.endsWith('@prontohousing.io')
  )?.email ?? '';

const primaryPhoneOf = (member: UHMember): string =>
  member.phones?.find((p) => p.is_primary)?.phone_number ?? '';

/**
 * Thin profile (UserHouseholdMember) editor. Mirrors the field set and
 * validation of AddUserHouseholdMemberScreen, but PATCHes the UHM endpoint and
 * is not cert-bound. Removal is handled by the composition modal (single DELETE
 * call site) via the member-list card.
 */
const EditUserHouseholdMemberScreen: FC<EditUserHouseholdMemberScreenProps> = ({
  member,
  onSaved,
  onCancel,
}) => {
  const isHead = member.household_role === 'hoh';

  const [values, setValues] = useState<FormValues>({
    first_name: member.first_name ?? '',
    middle_name: member.middle_name ?? '',
    last_name: member.last_name ?? '',
    household_role: member.household_role,
    preferred_language: null,
    email: primaryEmailOf(member),
    phone_number: primaryPhoneOf(member),
  });
  const [languages, setLanguages] = useState<Language[]>([]);
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    const fetchLanguages = async (): Promise<void> => {
      try {
        const res = await axios.get('/household/languages/');
        if (res.status === 200) {
          setLanguages(res.data as Language[]);
        }
      } catch {
        // non-fatal — language picker remains empty
      }
    };
    void fetchLanguages();
  }, []);

  // Reconcile the member's saved language name with the fetched options so the
  // Autocomplete shows the current value once options are available.
  useEffect(() => {
    if (!member.preferred_language || languages.length === 0) {
      return;
    }
    setValues((prev) =>
      prev.preferred_language
        ? prev
        : {
            ...prev,
            preferred_language:
              languages.find((l) => l.name === member.preferred_language) ??
              null,
          }
    );
  }, [languages, member.preferred_language]);

  const roleOptions = useMemo(() => {
    if (isHead) {
      return [{ value: 'hoh' as UhmRole, label: UHM_ROLE_LABELS.hoh }];
    }
    return UHM_EDITABLE_ROLE_OPTIONS;
  }, [isHead]);

  const validate = (): string | null => {
    if (!values.first_name.trim()) {
      return 'First Name is required.';
    }
    if (!values.last_name.trim()) {
      return 'Last Name is required.';
    }
    if (values.email.trim() && !emailRegex.test(values.email.trim())) {
      return 'A valid email address is required.';
    }
    return null;
  };

  const handleSave = async (): Promise<void> => {
    const validationError = validate();
    if (validationError) {
      setSubmitError(validationError);
      return;
    }
    setSubmitError(null);
    setSaving(true);
    try {
      const payload: Record<string, unknown> = {
        first_name: values.first_name.trim(),
        last_name: values.last_name.trim(),
        middle_name: values.middle_name.trim() || null,
        household_role: values.household_role,
      };
      if (values.email.trim()) {
        payload.email = values.email.trim();
      }
      if (values.phone_number.trim()) {
        payload.phone_number = values.phone_number.trim();
      }
      if (values.preferred_language) {
        payload.preferred_language = values.preferred_language.id;
      }
      await axios.patch(
        `v2/renter-hub/me/my-household/members/${member.user_household_member_id}/`,
        payload
      );
      onSaved();
    } catch (err: unknown) {
      setSubmitError(
        messageForHouseholdMemberError(
          err as { detail?: string; error_code?: string; status?: number },
          'Unable to save changes. Please try again.'
        )
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ ...cardSx, p: 3 }}>
      <Stack gap={2.5}>
        <Typography
          sx={{ fontSize: 16, fontWeight: 700, color: 'text.primary' }}
        >
          Edit Household Member
        </Typography>

        <Typography variant="body2" color="text.secondary">
          {getProfileMemberFullName(member)}
        </Typography>

        {submitError && (
          <Alert severity="error" onClose={() => setSubmitError(null)}>
            {submitError}
          </Alert>
        )}

        <TextField
          label="First Name"
          required
          fullWidth
          value={values.first_name}
          onChange={(e) => setValues({ ...values, first_name: e.target.value })}
          disabled={saving}
          size="small"
        />

        <TextField
          label="Middle Name"
          fullWidth
          value={values.middle_name}
          onChange={(e) =>
            setValues({ ...values, middle_name: e.target.value })
          }
          disabled={saving}
          size="small"
        />

        <TextField
          label="Last Name"
          required
          fullWidth
          value={values.last_name}
          onChange={(e) => setValues({ ...values, last_name: e.target.value })}
          disabled={saving}
          size="small"
        />

        <FormControl fullWidth size="small">
          <InputLabel id="profile-member-role-label">Role</InputLabel>
          <Select
            labelId="profile-member-role-label"
            label="Role"
            value={values.household_role}
            onChange={(e) =>
              setValues({
                ...values,
                household_role: e.target.value as UhmRole,
              })
            }
            disabled={saving || isHead}
          >
            {roleOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Autocomplete
          fullWidth
          size="small"
          options={languages}
          getOptionLabel={(option) => option.name}
          value={values.preferred_language}
          onChange={(_event, newValue) =>
            setValues({ ...values, preferred_language: newValue })
          }
          disabled={saving}
          renderInput={(params) => (
            <TextField {...params} label="Preferred Language" size="small" />
          )}
        />

        <TextField
          label="Email"
          type="email"
          fullWidth
          value={values.email}
          onChange={(e) => setValues({ ...values, email: e.target.value })}
          disabled={saving}
          size="small"
        />

        <TextField
          label="Phone Number"
          type="tel"
          placeholder="(555) 123-4567"
          fullWidth
          value={values.phone_number}
          onChange={(e) =>
            setValues({
              ...values,
              phone_number: formatPhoneNumber(e.target.value),
            })
          }
          disabled={saving}
          size="small"
        />

        <Stack direction="row" gap={1.5} justifyContent="flex-end">
          <Button
            variant="outlined"
            onClick={onCancel}
            disabled={saving}
            sx={{ borderRadius: '8px', fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disableElevation
            onClick={() => void handleSave()}
            disabled={saving}
            startIcon={
              saving ? (
                <CircularProgress size={16} color="inherit" />
              ) : undefined
            }
            sx={{ borderRadius: '8px', fontWeight: 600 }}
          >
            <span>{saving ? 'Saving…' : 'Save changes'}</span>
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

export default EditUserHouseholdMemberScreen;
